import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MessagingAPI, resolveImage, formatRelative } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Send, Image as ImageIcon, ArrowLeft, MessageSquare, Package, Tag, ShoppingBag, Check, CheckCheck, Search } from 'lucide-react';
import { SupplierAPI } from '../lib/api';

const presenceText = (lastSeen) => {
  if (!lastSeen) return null;
  const diff = (Date.now() - new Date(lastSeen).getTime()) / 1000;
  if (diff < 120) return { online: true, text: 'En ligne' };
  if (diff < 3600) return { online: false, text: `Vu il y a ${Math.floor(diff / 60)} min` };
  if (diff < 86400) return { online: false, text: `Vu il y a ${Math.floor(diff / 3600)} h` };
  return { online: false, text: `Vu il y a ${Math.floor(diff / 86400)} j` };
};

const ctxIcon = (context) => {
  if (context?.startsWith('order:')) return <ShoppingBag className="w-3.5 h-3.5" />;
  if (context?.startsWith('product:')) return <Package className="w-3.5 h-3.5" />;
  if (context?.startsWith('listing:')) return <Tag className="w-3.5 h-3.5" />;
  return <MessageSquare className="w-3.5 h-3.5" />;
};

const Messages = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [params, setParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(params.get('c') || null);
  const [active, setActive] = useState(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingConv, setLoadingConv] = useState(false);
  const [convSearch, setConvSearch] = useState('');
  const bottomRef = useRef(null);
  const fileRef = useRef(null);
  const QUICK_REPLIES = [
    'Disponible ✓',
    'En rupture pour le moment',
    'Je vous livre demain',
    'Quel est votre adresse exacte ?',
    'Merci pour votre commande !',
    'Pouvez-vous préciser la quantité ?',
  ];

  const loadConversations = useCallback(() => {
    MessagingAPI.conversations().then(setConversations).catch(() => {});
  }, []);

  const loadActive = useCallback((cid) => {
    if (!cid) return;
    MessagingAPI.conversation(cid).then((d) => {
      setActive((prev) => {
        // Scroller seulement si de nouveaux messages sont arrivés
        const prevCount = prev?.messages?.length || 0;
        const newCount = d?.messages?.length || 0;
        if (newCount > prevCount) {
          setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
        }
        return d;
      });
    }).catch(() => {});
  }, []);

  // Chargement initial
  useEffect(() => { loadConversations(); }, [loadConversations]);

  // Ouvrir la conversation demandée dans l'URL
  useEffect(() => {
    if (activeId) {
      setLoadingConv(true);
      MessagingAPI.conversation(activeId).then((d) => {
        setActive(d); setLoadingConv(false);
        setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'auto' }), 50);
      }).catch(() => setLoadingConv(false));
    }
  }, [activeId]);

  // Polling adaptatif : 4s quand l'onglet est actif, pause quand il est caché
  useEffect(() => {
    if (!activeId) return;
    const tick = () => { if (!document.hidden) loadActive(activeId); };
    const t1 = setInterval(tick, 4000);
    // Rafraîchir immédiatement quand l'utilisateur revient sur l'onglet
    const onVisible = () => { if (!document.hidden) loadActive(activeId); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(t1); document.removeEventListener('visibilitychange', onVisible); };
  }, [activeId, loadActive]);

  useEffect(() => {
    const tick = () => { if (!document.hidden) loadConversations(); };
    const t2 = setInterval(tick, 8000);
    return () => clearInterval(t2);
  }, [loadConversations]);

  const openConv = (cid) => {
    setActiveId(cid);
    setParams({ c: cid });
  };

  const sendQuick = async (msg) => {
    if (sending || !activeId) return;
    setSending(true);
    const optimistic = {
      id: 'tmp_' + Date.now(), from_id: user.user_id, to_id: active?.peer?.user_id,
      text: msg, image: '', read: false, created_at: new Date().toISOString(),
    };
    setActive((a) => a ? { ...a, messages: [...a.messages, optimistic] } : a);
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    try { await MessagingAPI.send(activeId, msg); loadActive(activeId); loadConversations(); }
    catch (e) { toast({ title: 'Erreur', description: 'Message non envoyé' }); }
    setSending(false);
  };

  const doSend = async () => {
    const t = text.trim();
    if (!t || sending || !activeId) return;
    setSending(true);
    setText('');
    // Optimistic : ajouter le message localement
    const optimistic = {
      id: 'tmp_' + Date.now(), from_id: user.user_id, to_id: active?.peer?.user_id,
      text: t, image: '', read: false, created_at: new Date().toISOString(),
    };
    setActive((a) => a ? { ...a, messages: [...a.messages, optimistic] } : a);
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    try {
      await MessagingAPI.send(activeId, t);
      loadActive(activeId);
      loadConversations();
    } catch (e) {
      toast({ title: 'Erreur', description: 'Message non envoyé' });
    }
    setSending(false);
  };

  const sendImage = async (file) => {
    if (!file || !activeId) return;
    setSending(true);
    try {
      const res = await SupplierAPI.uploadImage(file);
      const url = res.url || (res.file_id ? `/api/files/${res.file_id}` : '');
      await MessagingAPI.send(activeId, '', url);
      loadActive(activeId);
      loadConversations();
    } catch (e) { toast({ title: 'Erreur', description: 'Image non envoyée' }); }
    setSending(false);
  };

  if (!user) {
    return <div className="max-w-2xl mx-auto px-4 py-16 text-center text-[#7A6F5C]">Connectez-vous pour accéder à votre messagerie.</div>;
  }

  return (
    <div className="max-w-6xl mx-auto px-2 sm:px-4 py-4">
      <h1 className="font-serif text-2xl font-semibold text-[#1F4D3F] mb-4 px-2">Messagerie</h1>
      <div className="bg-white border border-[#E8E0D0] rounded-2xl overflow-hidden flex" style={{ height: '70vh', maxHeight: '70vh' }}>
        {/* Liste des conversations */}
        <div className={`${active ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-80 md:flex-shrink-0 border-r border-[#E8E0D0] overflow-y-auto`}>
          <div className="p-2 border-b border-[#F5F1EA] sticky top-0 bg-white z-10">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9A8F82]" />
              <input value={convSearch} onChange={(e) => setConvSearch(e.target.value)} placeholder="Rechercher…"
                className="w-full pl-9 pr-3 py-2 border border-[#E8E0D0] rounded-lg text-sm focus:outline-none focus:border-[#1F4D3F]" />
            </div>
          </div>
          {conversations.length === 0 ? (
            <div className="p-6 text-center">
              <div className="w-14 h-14 rounded-full bg-[#F5F1EA] flex items-center justify-center mx-auto mb-3">
                <MessageSquare className="w-7 h-7 text-[#1F4D3F]" />
              </div>
              <p className="text-sm font-medium text-[#2D2A26] mb-1">Aucune conversation</p>
              <p className="text-xs text-[#7A6F5C]">Contactez un fournisseur depuis une fiche produit ou une commande pour démarrer une discussion ici.</p>
            </div>
          ) : conversations.filter((c) => {
            const q = convSearch.trim().toLowerCase();
            if (!q) return true;
            return (c.peer?.name || '').toLowerCase().includes(q) || (c.context_label || '').toLowerCase().includes(q) || (c.last_message || '').toLowerCase().includes(q);
          }).map((c) => (
            <button key={c.id} onClick={() => openConv(c.id)}
              className={`text-left p-3 border-b border-[#F5F1EA] hover:bg-[#FAF7F2] transition ${activeId === c.id ? 'bg-[#FAF7F2]' : ''}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-[#2D2A26] truncate">{c.peer?.name || 'Utilisateur'}</span>
                {c.unread > 0 && <span className="bg-[#C75B27] text-white text-[10px] rounded-full px-1.5 py-0.5 min-w-[18px] text-center">{c.unread}</span>}
              </div>
              <div className="flex items-center gap-1 text-[10px] text-[#1F4D3F] mt-0.5">
                {ctxIcon(c.context)} <span className="truncate">{c.context_label}</span>
              </div>
              <div className="text-xs text-[#7A6F5C] truncate mt-1">
                {c.last_from === user.user_id ? 'Vous : ' : ''}{c.last_message}
              </div>
            </button>
          ))}
        </div>

        {/* Vue conversation */}
        <div className={`${active ? 'flex' : 'hidden md:flex'} flex-col flex-1 min-w-0 overflow-hidden`}>
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-sm text-[#7A6F5C]">
              Sélectionnez une conversation
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 p-3 border-b border-[#E8E0D0] bg-[#FAF7F2]">
                <button onClick={() => { setActive(null); setActiveId(null); setParams({}); }} className="text-[#1F4D3F]" title="Retour à la liste"><ArrowLeft className="w-5 h-5" /></button>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-[#2D2A26] truncate flex items-center gap-2">
                    {active.peer?.name}
                    {(() => {
                      const p = presenceText(active.peer?.last_seen);
                      return p ? <span className={`text-[10px] font-normal flex items-center gap-1 ${p.online ? 'text-emerald-600' : 'text-[#9A8F82]'}`}>
                        {p.online && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />}{p.text}
                      </span> : null;
                    })()}
                  </div>
                  <div className="flex items-center gap-1 text-[10px] text-[#1F4D3F]">{ctxIcon(active.context)} <span className="truncate">{active.context_label}</span></div>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-[#FDFCFA]">
                {active.messages.map((m) => {
                  const mine = m.from_id === user.user_id;
                  return (
                    <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[75%] rounded-2xl px-3 py-2 ${mine ? 'bg-[#1F4D3F] text-white rounded-br-sm' : 'bg-white border border-[#E8E0D0] text-[#2D2A26] rounded-bl-sm'}`}>
                        {m.image && <img src={resolveImage(m.image)} alt="" className="rounded-lg mb-1 max-w-full" />}
                        {m.text && <div className="text-sm whitespace-pre-wrap break-words">{m.text}</div>}
                        <div className={`text-[10px] mt-1 flex items-center gap-1 ${mine ? 'text-white/70 justify-end' : 'text-[#9A8F82]'}`}>
                          {formatRelative(m.created_at)}
                          {mine && (m.read ? <CheckCheck className="w-3.5 h-3.5" /> : <Check className="w-3.5 h-3.5" />)}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>

              <div className="px-3 pt-2 flex gap-2 overflow-x-auto border-t border-[#F5F1EA]">
                {QUICK_REPLIES.map((q) => (
                  <button key={q} onClick={() => sendQuick(q)} disabled={sending}
                    className="whitespace-nowrap text-xs bg-[#F5F1EA] hover:bg-[#E8E0D0] text-[#1F4D3F] px-3 py-1.5 rounded-full border border-[#E8E0D0] flex-shrink-0">
                    {q}
                  </button>
                ))}
              </div>
              <div className="p-3 border-t border-[#E8E0D0] flex items-center gap-2">
                <button onClick={() => fileRef.current?.click()} className="text-[#7A6F5C] hover:text-[#1F4D3F] p-2" title="Envoyer une photo">
                  <ImageIcon className="w-5 h-5" />
                </button>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => sendImage(e.target.files[0])} />
                <input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); doSend(); } }}
                  placeholder="Écrivez votre message…"
                  className="flex-1 border border-[#E8E0D0] rounded-full px-4 py-2 text-sm focus:outline-none focus:border-[#1F4D3F]"
                />
                <button onClick={doSend} disabled={sending || !text.trim()}
                  className="bg-[#1F4D3F] hover:bg-[#173B30] disabled:bg-gray-300 text-white rounded-full p-2.5">
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Messages;
